import React, { useState } from "react";

interface SummaryChip {
  title: string;
  meta: string;
  main: string;
  footer: string;
  badge: {
    text: string;
    color: "blue" | "amber" | "red" | "green";
  };
}

const summaryData: SummaryChip[] = [
  {
    title: "ON + BC vs Prairies",
    meta: "Weighted default rate",
    main: "-0.6 pp",
    footer:
      "Ontario + BC portfolios are running about 60 bps lower defaults than AB/MB/SK over the last 12M.",
    badge: { text: "Lower risk", color: "blue" },
  },
  {
    title: "Concentration",
    meta: "Top 3 metros",
    main: "Toronto / Van / Montreal",
    footer:
      "~62% of portfolio balance sits in the three largest CMAs – still within target band of 55–70%.",
    badge: { text: "62% exposure", color: "amber" },
  },
  {
    title: "Watchlist band",
    meta: "High-risk geos",
    main: "Halifax · AB mid-markets",
    footer:
      "Defaults > 3.8% in Halifax and selected AB mid-markets. Risk committee flagged for tighter underwriting.",
    badge: { text: "Escalated", color: "red" },
  },
];

export const RiskFilters: React.FC = () => {
  const [selectedMetric, setSelectedMetric] = useState("Default rate");
  const [selectedHorizon, setSelectedHorizon] = useState("Last 12M");
  const [selectedProfile, setSelectedProfile] = useState("Owner-occupied");
  const [selectedProvince, setSelectedProvince] = useState("National");

  const metrics = ["Default rate", "Avg LTV", "Avg coupon"];
  const horizons = ["Last 12M", "Last 24M", "Since origination"];
  const profiles = ["Owner-occupied", "Investor", "Self-employed"];
  const provinces = ["National", "ON", "BC", "AB", "QC"];

  const getBadgeClasses = (color: string) => {
    switch (color) {
      case "blue":
        return "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]";
      case "amber":
        return "border-[rgba(255,184,0,0.8)] text-[#ffb800] bg-[rgba(255,184,0,0.1)]";
      case "red":
        return "border-[rgba(255,51,102,0.8)] text-[#ff3366] bg-[rgba(255,51,102,0.1)]";
      case "green":
        return "border-[rgba(0,255,136,0.8)] text-neon-green bg-[rgba(0,255,136,0.12)]";
      default:
        return "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]";
    }
  };

  return (
    <div className="relative p-4 pb-4.5 rounded-[18px] bg-gradient-to-br from-[#22284d] to-[#10142f] border border-[rgba(139,146,176,0.2)] shadow-[0_16px_40px_rgba(0,0,0,0.6)] overflow-hidden">
      <div className="flex items-center justify-between gap-2 mb-2.5">
        <div className="flex flex-col gap-0.5">
          <div className="text-[15px] font-medium tracking-[0.06em] uppercase">
            Risk filters
          </div>
          <div className="text-[11px] text-[#8b92b0]">
            Toggle what drives risk: metric, horizon, and borrower segment.
          </div>
        </div>
        <span className="px-2.25 py-0.75 rounded-full text-[10px] uppercase tracking-[0.12em] border border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.12)]">
          Interactive
        </span>
      </div>

      <div className="grid grid-cols-1 gap-2.5 text-xs">
        <div>
          <div className="text-[11px] text-[#8b92b0] mb-1">Metric</div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {metrics.map((metric) => (
              <span
                key={metric}
                onClick={() => setSelectedMetric(metric)}
                className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                  selectedMetric === metric
                    ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                    : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)]"
                }`}
              >
                {metric}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] text-[#8b92b0] mb-1">Time horizon</div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {horizons.map((horizon) => (
              <span
                key={horizon}
                onClick={() => setSelectedHorizon(horizon)}
                className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                  selectedHorizon === horizon
                    ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                    : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)]"
                }`}
              >
                {horizon}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] text-[#8b92b0] mb-1">
            Borrower profile
          </div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {profiles.map((profile) => (
              <span
                key={profile}
                onClick={() => setSelectedProfile(profile)}
                className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                  selectedProfile === profile
                    ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                    : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)]"
                }`}
              >
                {profile}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[11px] text-[#8b92b0] mb-1">Province</div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {provinces.map((province) => (
              <span
                key={province}
                onClick={() => setSelectedProvince(province)}
                className={`px-3 py-1.5 rounded-full border cursor-pointer text-[11px] transition-all duration-200 ${
                  selectedProvince === province
                    ? "border-[rgba(0,212,255,0.8)] text-cyber-blue bg-[rgba(0,212,255,0.1)]"
                    : "border-[rgba(139,146,176,0.7)] bg-[rgba(5,7,22,0.9)] text-[#8b92b0] hover:bg-[rgba(26,31,58,0.9)]"
                }`}
              >
                {province}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2.5 mt-2">
        {summaryData.map((summary, idx) => (
          <div
            key={idx}
            className="p-3.5 rounded-[12px] bg-[rgba(5,7,22,0.95)] border border-[rgba(139,146,176,0.5)] text-xs flex flex-col gap-1.5"
          >
            <div className="flex items-center justify-between gap-1.5">
              <div>
                <div className="text-xs font-medium">{summary.title}</div>
                <div className="text-[11px] text-[#8b92b0]">{summary.meta}</div>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-[10px] border ${getBadgeClasses(
                  summary.badge.color
                )}`}
              >
                {summary.badge.text}
              </span>
            </div>
            <div className="text-[17px] font-jetbrains-mono font-semibold">
              {summary.main}
            </div>
            <div className="text-[11px] text-[#8b92b0]">{summary.footer}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
